import { SendOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import { TextAreaRef } from "antd/lib/input/TextArea";
import React, { ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { InputContainerProps, InputInterfaceRef } from "../containers/InputContainer";
import InputAddons from "./InputAddons";

export interface SimpleTextInputProps extends InputContainerProps {
  isChatOnlyMode: boolean;
  isAgentSpeaking: boolean;
  sendTextMessage(text: string): void;
}

export default function SimpleTextInput({ componentRef, showQuickResponses, showSuggestedAnswer, onShowQuickResponses, onShowSuggestedAnswer, isChatOnlyMode, isAgentSpeaking, sendTextMessage }: SimpleTextInputProps): ReactElement {
  const { t } = useTranslation();

  const [typedText, setTypedText] = useState("");
  const [showWarningStatus, setShowWarningStatus] = useState(false);

  const showWarningTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleSend = () => {
    if (typedText.trim() === "") {
      setShowWarningStatus(true);

      if (showWarningTimeout.current)
        clearTimeout(showWarningTimeout.current);

      // hide warning after short time
      showWarningTimeout.current = setTimeout(
        () => { setShowWarningStatus(false); showWarningTimeout.current = null; },
        300,
      );

      return;
    }

    sendTextMessage(typedText);
    setTypedText("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) { // if enter is pressed without any modifiers
      handleSend();
      e.preventDefault();
    }
  };

  const inputRef = useRef<TextAreaRef>(null);

  if (componentRef) {
    (componentRef as React.MutableRefObject<InputInterfaceRef>).current = {
      setInputTextAndFocus: (text) => {
        setTypedText(text);
        inputRef.current?.focus();
      },
    };
  }

  return (
    <RootBox>
      <InputAddons
        isChatOnlyMode={isChatOnlyMode}
        isAgentSpeaking={isAgentSpeaking}
        showQuickResponses={showQuickResponses}
        showSuggestedAnswer={showSuggestedAnswer}
        onShowQuickResponses={onShowQuickResponses}
        onShowSuggestedAnswer={onShowSuggestedAnswer}
      />
      <Input.TextArea
        ref={inputRef}
        status={showWarningStatus ? "warning" : undefined}
        value={typedText}
        onChange={e => setTypedText(e.target.value)}
        onKeyDown={handleKeyDown}
        autoSize={{ minRows: 1, maxRows: 5 }}
        placeholder={t("input.inputPlaceholder")}
      />
      <Button
        type="link"
        icon={<SendOutlined />}
        disabled={typedText.trim() === ""}
        onClick={handleSend}
        aria-label={t("input.send")}
        style={{ fontSize: "1.1em" }}
      />
    </RootBox>
  );
}

const RootBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: ${p => p.theme.paddingXXS}px;

  & > * {
    flex: 0 0 auto;
  }

  & > :nth-child(2) {
    flex: 1 1 auto;
  }
`;
